import styled from '@emotion/styled';
import React, { memo, useEffect } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ClickAwayListener, Popper } from '@mui/material';
import ChipVariant from 'components/ChipVariant';

const BtnDropdown = styled.button<{ readonly?: boolean }>`
  background-color: transparent;
  color: black;
  padding: 5px;
  border: ${props => props.readonly ? 'none' : '1px solid #ccc'};
  border-radius: 8px;
  cursor: ${props => props.readonly ? 'default' : 'pointer'};
  pointer-events: ${props => props.readonly ? 'none' : 'auto'};
  position: relative;
  width: 100%;

  &:hover {
    border: ${props => props.readonly ? 'none' : '1.8px solid #4b68ef'};
  }

  .dropdown-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;

    & .dropdown-placeholder {
      font-size: 14px;
      margin-right: 8px;
      color: #c1c1c1ff;
    }
    & .dropdown-arrow {
      font-size: 20px;
      color: #c1c1c1ff;
    }
  }
`;

const DropdownContents = styled.div`
  display: block;
  // position: absolute;
  // top: 100%;
  // left: 0;
  // z-index: 1000;
  margin-top: 4px;

  // tambahkan style agar posisi dropdown-content dinamis dapat dibawah atau diatas tombol sesuai ruang yang tersedia
  &.dropup {
    bottom: 100%;
    top: auto;
    margin-bottom: 4px;
  }
`;

const SelectedProduct = styled.div<{ readonly?: boolean }>`
  display: flex;
  margin-right: 10px;
  padding: 8px;

  &.recommend-item {
    padding: 8px;
    margin-right: 0;

    &:hover {
      background-color: #f5f5f5;
      cursor: ${props => props.readonly ? 'default' : 'pointer'};
    }
  }

  img {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 4px;
    margin-right: 8px;
  }

  .content-selected-product {
    margin-right: 16px;

    & p {
      font-size: 12px;
    }
  }

  .score-selected-item {
    padding-right: 10px;
    font-size: 12px;
  }

  span {
    font-size: 12px;
    color: #333;
  }
`;

interface DefaultOptionData {
  channel_name: string;
  final_score: number;
  image_score: 1;
  img_url: string;
  item_code: string;
  item_name: string;
  item_name_score: string;
  store_name: string;
  variation_score: number;
  variation_values: { label: string; value: string }[] | null;
  product_link: string | undefined;
}

const DefaultOptionDataInitial: DefaultOptionData = {
  channel_name: '',
  final_score: 0,
  image_score: 1,
  img_url: '',
  item_code: '',
  item_name: '',
  item_name_score: '',
  store_name: '',
  variation_score: 0,
  variation_values: null,
  product_link: undefined
};

const FieldInputNewSKU = styled.div`
  max-width: 960px;
  padding: 10px;

  /* Input garis bawah */
  .underline-input {
    width: 100%;
    border: none;
    border-bottom: 1px solid #cfcfcf;
    background: transparent;
    padding: 6px 0 10px;
    font-weight: 400;
    line-height: 1.2;
    /* Ukuran teks besar dan responsif seperti contoh */
    // font-size: clamp(24px, 6vw, 48px);
    color: #222;
    outline: none;
    font-size: 12px;
  }

  .underline-input::placeholder {
    color: #bbb; /* abu-abu muda seperti contoh */
  }

  .underline-input:focus {
    border-bottom-color: #a8a8a8; /* sedikit lebih gelap saat fokus */
  }

  /* Label aksesibel tapi disembunyikan visual */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
    font-size: 12px;
  }
`;

const InputNewSKU = ({ onEnter }: { onEnter: (value: string) => void }) => {
  return (
    <FieldInputNewSKU>
      <label htmlFor="sku" className="sr-only">
        Masukkan SKU baru
      </label>
      <input
        id="sku"
        type="text"
        className="underline-input"
        placeholder="Masukkan SKU baru"
        maxLength={50}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter' && onEnter) {
            // contoh: console.log('SKU baru:', (e.target as HTMLInputElement).value);
            onEnter((e.target as HTMLInputElement).value);
          }
        }}
      />
    </FieldInputNewSKU>
  );
};

const PlaceHolderExample = ({
  item,
  selectedItem,
  onClick,
  ...rest
}: {
  children?: React.ReactNode;
  item?: DefaultOptionData;
  onClick?: () => void;
  [x: string]: unknown;
  selectedItem?: DefaultOptionData | null;
}) => {
  if (!selectedItem && !item) {
    return (
      <SelectedProduct {...rest} onClick={onClick}>
        <span
          style={{
            color: '#c1c1c1',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          Pilih Rekomendasi SKU
        </span>
      </SelectedProduct>
    );
  }

  const itemData = item || selectedItem;
  return (
    <SelectedProduct {...rest} onClick={onClick}>
      {itemData.img_url && (
        <img className="img-selected-product" src={itemData.img_url} alt="placeholder" />
      )}
      <div className="content-selected-product text-left">
        {itemData.item_name && (
          <p
            className="m-0"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '200px'
            }}
          >
            {itemData.item_name}
          </p>
        )}
        {itemData.item_code && <p className="m-0">{itemData.item_code}</p>}
        {itemData.variation_values && itemData.variation_values?.map((variant) => (
          <ChipVariant key={variant.value} value={variant.value} />
        ))}
        {itemData.product_link && (
          <p className="m-0">
            <a href="#">{itemData.product_link}</a>
          </p>
        )}
      </div>
      {item && (
        <div>
          <p className="m-0 score-selected-item">{((item?.final_score || 0) * 100).toFixed(2)}%</p>
        </div>
      )}
    </SelectedProduct>
  );
};

const DropdownRecommendation = ({ data, onSelection, selected, readonly }) => {
  const [open, setOpen] = React.useState(false);
  const [isDropup, setIsDropup] = React.useState(false);
  const [dropdownPosition, setDropdownPosition] = React.useState({ top: 0, left: 0, width: 0 });
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [selectedItem, setSelectedItem] = React.useState<DefaultOptionData | null>(selected || null);

  useEffect(() => {
    if (selected) {
      setSelectedItem(selected);
    }
  }, [data, selected])

  const handleClickAway = () => {
    setOpen(false);
  };

  const handleToggle = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 300; // estimasi tinggi dropdown

      // Jika ruang di bawah tidak cukup dan ruang di atas lebih besar, gunakan dropup
      const shouldDropup = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      setIsDropup(shouldDropup);

      // Set posisi dropdown berdasarkan posisi button
      setDropdownPosition({
        top: shouldDropup ? rect.top - dropdownHeight : rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
    setOpen((prev) => !prev);
  };

  return (
    <>
      <ClickAwayListener onClickAway={() => handleClickAway()}>
        <div>
          <BtnDropdown
            ref={buttonRef}
            type="button"
            onClick={() => handleToggle()}
            className={isDropup ? 'dropup' : ''}
            readonly={readonly}
          >
            <div className="dropdown-wrapper">
              {/* <span className='dropdown-placeholder'>Pilih Rekomendasi SKU</span> */}
              <PlaceHolderExample readonly={readonly} selectedItem={selectedItem} />
              {!readonly && <KeyboardArrowDownIcon className="dropdown-arrow" />}
            </div>
          </BtnDropdown>
          <Popper
            open={open}
            anchorEl={buttonRef.current}
            placement={isDropup ? 'top-end' : 'bottom-end'}
            style={{ zIndex: 1300 }}
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [50, 0]
                }
              }
            ]}
          >
            <DropdownContents>
              <div
                style={{
                  // padding: '10px',
                  minWidth: '200px',
                  maxWidth: '500px',
                  backgroundColor: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  borderRadius: '4px'
                }}
              >
                {/* <p style={{ margin: 0 }}>This is the dropdown content.</p> */}
                {data.length > 0 ? data?.map((item, idx) => (
                  <PlaceHolderExample
                    key={idx}
                    className="recommend-item"
                    item={item}
                    onClick={() => {
                      item.selected = true;
                      setSelectedItem(item);
                      if (onSelection) {
                        onSelection(item);
                      }
                      setOpen(false);
                    }}
                  />
                )) : (
                  <p
                    style={{
                      color: '#959595',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      margin: 0,
                      padding: '8px 10px 0',
                      fontSize: '12px',
                      textAlign: 'center'
                    }}
                  >
                    Rekomendasi tidak ditemukan
                  </p>
                )}
                <InputNewSKU
                  onEnter={(value) => {
                    setSelectedItem({
                      ...DefaultOptionDataInitial,
                      item_code: value
                    });
                    if (onSelection) {
                      onSelection({
                        ...DefaultOptionDataInitial,
                        item_code: value,
                        type: 'freeText'
                      });
                    }
                    setOpen(false);
                  }}
                />
              </div>
            </DropdownContents>
          </Popper>
        </div>
      </ClickAwayListener>
    </>
  );
};

export default memo(DropdownRecommendation);
